import { Component, HostBinding, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ContextService,
  SessionStorageService,
  OnPremLoginDetails,
} from '@maximizer/core/shared/data-access';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import { GlobalServicesLoginDetails } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  OutlookInsightsEvents,
  getDecodedAccessToken,
  ON_PREM_CLIENT_ID,
} from '@maximizer/outlook/shared/data-access';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { catchError, of } from 'rxjs';
import { LoginType } from '@maximizer/outlook/shared/ui';
import { normalizeError } from '@maximizer/shared/util';

@Component({
  selector: 'maximizer-outlook-authorize',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export class OutlookAuthorizeComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  loginType: LoginType = LoginType.Cloud;
  cloudLoginDetails?: GlobalServicesLoginDetails;
  onPremLoginDetails?: OnPremLoginDetails;

  constructor(
    @Inject(ON_PREM_CLIENT_ID) private readonly onPremClientId: string,
    private readonly oauthService: OAuth2Service,
    private readonly sessionStorage: SessionStorageService,
    private readonly route: ActivatedRoute,
    private readonly insightsService: InsightsService,
    private readonly contextService: ContextService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const errorParam = params['error'];
      if (errorParam) {
        this.handleError(
          'authorize-init-error',
          'Authentication error detected in query parameters.',
          { errorParam },
        );
        return;
      }

      const code: string = params['code'];
      const state: string = params['state'];
      const loginHint: string = params['login_hint'];
      const sessionAuth = this.sessionStorage.get('auth_code');
      const url = this.sessionStorage.get('url');
      const isCloud = this.isCloud();

      this.logAuthDetailsToInsights('authorize-init-start', {
        hasCode: !!code,
        hasState: !!state,
        hasLoginHint: !!loginHint,
        hasSessionAuth: !!sessionAuth,
        hasUrl: !!url,
        isCloud,
      });

      if (!sessionAuth || !url || !code || !state) {
        this.handleError(
          'authorize-missing-parameters',
          'Missing required authorization parameters',
          {
            missingSessionAuth: !sessionAuth,
            missingUrl: !url,
            missingCode: !code,
            missingState: !state,
          },
        );
        return;
      }

      let auth_code: {
        url: string;
        state: string;
        code_challenge: string;
        original_code: string;
      };

      try {
        auth_code = JSON.parse(sessionAuth);
      } catch (error) {
        this.handleError(
          'authorize-auth-code-parse-error',
          'Failed to parse auth code from session storage',
          undefined,
          error,
        );
        return;
      }

      if (auth_code.state !== state) {
        this.handleError(
          'authorize-state-mismatch',
          'OAuth state parameter mismatch',
        );
        return;
      }

      if (isCloud) {
        const loginDetailsObj = this.sessionStorage.get('login_details');
        if (!loginDetailsObj) {
          this.handleError(
            'authorize-cloud-missing-login-details',
            'Missing cloud login details in session storage',
          );
          return;
        }

        try {
          this.cloudLoginDetails = JSON.parse(
            loginDetailsObj,
          ) as GlobalServicesLoginDetails;

          this.oauthService.cloudLoginDetails = this.cloudLoginDetails;
          this.logAuthDetailsToInsights('authorize-cloud-setup-success');
        } catch (error) {
          this.handleError(
            'authorize-cloud-parse-error',
            'Failed to parse cloud login details',
            undefined,
            error,
          );
          return;
        }
      } else {
        const onPremHost = this.sessionStorage.get('on_prem_host') ?? '';
        const onPremInfo = {
          host: onPremHost,
          clientId: this.onPremClientId,
          database: '',
        };

        this.onPremLoginDetails = onPremInfo;
        this.oauthService.onPremLoginDetails = onPremInfo;
      }

      this.oauthService
        .getToken(
          code,
          `${url}/authorize`,
          auth_code.original_code,
          loginHint,
          false,
          isCloud,
        )
        .pipe(
          catchError((error) => {
            this.logError('login-error', error);
            return of(null);
          }),
        )
        .subscribe((data) => {
          if (data === null) {
            this.handleError(
              'authorize-token-response-null',
              'Received null response from OAuth token request.',
            );
            return;
          }

          this.logAuthDetailsToInsights('authorize-token-request-success');

          const messageObject = {
            messageType: 'success',
            auth: data,
            db: isCloud ? this.cloudLoginDetails : this.onPremLoginDetails,
            isCloud: isCloud,
          };
          this.sendMessageToParent(messageObject);
        });
    });
  }

  private handleError(
    name: string,
    error: string,
    metadata?: Record<string, unknown>,
    originalError?: unknown,
  ): void {
    const messageObject = { messageType: 'dialogClosed' };
    this.sendMessageToParent(messageObject);
    this.logError(name, originalError ?? new Error(error), metadata);
  }

  private logError(
    eventName: string,
    error: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    const errorMessage = normalizeError(error);
    const errorObj = error instanceof Error ? error : new Error(errorMessage);

    this.insightsService.trackException({
      error: errorObj,
      properties: {
        component: 'OutlookAuthorizeComponent',
        event: eventName,
        errorMessage,
        ...(metadata ? { metadata } : {}),
      },
    });
  }

  private logAuthDetailsToInsights(
    name: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.insightsService.trackEvent({ name }, SeverityLevel.Verbose, {
      eventId: OutlookInsightsEvents.OutlookAuth,
      decodedToken: getDecodedAccessToken(this.contextService.token),
      ...metadata,
    });
  }

  private sendMessageToParent(message: object) {
    const jsonMessage = JSON.stringify(message);
    if (typeof Office !== 'undefined' && Office?.context?.ui) {
      Office.context.ui.messageParent(jsonMessage);
    } else if (window.opener) {
      const origin = window.location.origin;
      window.opener.postMessage({ type: 'outlook', auth: jsonMessage }, origin);
    }
  }

  private isCloud(): boolean {
    const loginType = this.sessionStorage.get('login_type');

    if (loginType && loginType === LoginType.OnPrem) {
      return false;
    } else {
      return true;
    }
  }
}
